<template>
    <a-button @click="visible = true" class="tw-hidden md:tw-flex" left-icon="heroicons:plus-16-solid">{{
            $t("buttons.create") }}</a-button>
        <a-button @click="visible = true" class="md:tw-hidden" icon="heroicons:plus-16-solid"></a-button>
    <a-popup :visible="visible" @cancel="onCancel">
        <div
            class="tw-mx-auto tw-w-[95%] md:tw-max-w-[800px]  tw-h-fit tw-overflow-auto tw-flex tw-flex-col tw-border tw-border-solid dark:tw-border-gray-400 tw-border-gray-300 tw-shadow-md tw-my-16 tw-rounded">
            <div>
                <div
                    class="tw-p-4 tw-text-lg tw-flex tw-items-center tw-gap-2 tw-border-b tw-border-solid tw-font-semibold dark:tw-text-gray-700 tw-text-gray-500 dark:tw-bg-gray-100 tw-bg-gray-50">
                    <span>{{ $t("common.sourcing_request") }}</span>
                </div>

                <div class="tw-p-5 tw-bg-white tw-flex-1">
                    <div class="tw-grid tw-grid-cols-12 tw-gap-x-4 tw-gap-y-0">

                        <div class="tw-col-span-12 md:tw-col-span-12 tw-pb-4">
                            <h1 class="tw-ps-4 tw-border-s-2 tw-border-solid tw-border-primary-500">{{
                                $t('common.sourcing_type') }}</h1>
                        </div>

                        <div class="tw-col-span-12 md:tw-col-span-12 tw-pb-4">
                            <div class="tw-flex tw-gap-2">
                                <a-button 
                                    @click="sourcing.sourcing_type = 'new_product'" 
                                    class="tw-flex-1" 
                                    size="md"
                                    :intent="sourcing.sourcing_type === 'new_product' ? 'primary' : 'simple'"
                                    left-icon="heroicons:plus-circle-16-solid"
                                >
                                    {{ $t('sourcing.types.new_product') }}
                                </a-button>
                                <a-button 
                                    @click="sourcing.sourcing_type = 'restock'" 
                                    class="tw-flex-1" 
                                    size="md"
                                    :intent="sourcing.sourcing_type === 'restock' ? 'primary' : 'simple'"
                                    left-icon="heroicons:arrow-path-16-solid"
                                >
                                    {{ $t('sourcing.types.restock') }}
                                </a-button>
                            </div>
                            <div v-if="errors.sourcing_type" class="tw-text-red-500 tw-text-sm tw-mt-1">
                                {{ $t('errors.' + errors.sourcing_type, { field: $t('fields.sourcing_type') }) }}
                            </div>
                        </div>

                        <div v-if="sourcing.sourcing_type === 'restock'" class="tw-col-span-12 md:tw-col-span-12">
                            <FormVueSelect v-model="sourcing.product_id" :loading="isLoadingProducts" max-height="200" option-label="name" option-value="id" :options="productsList"
                                :label="$t('fields.select_product')" type="text" :error="errors.product_id &&
                                    $t('errors.' + errors.product_id, {
                                        field: $t('fields.select_product'),
                                    })
                                    ">
                                <template v-slot:option="{ option }">
                                    <div>
                                        <p class="tw-max-w-[135px] tw-truncate tw-text-xs">{{ option.name }}</p>
                                    </div>
                                </template>
                                <template v-slot:no-options>
                                    <p v-if="isLoadingProducts" class="tw-p-2 tw-text-center tw-text-sm tw-text-gray-500">{{ $t('messages.loading') }}</p>
                                    <p v-else class="tw-p-2 tw-text-center tw-text-sm tw-text-gray-500">{{ $t('messages.table-no-results') }}</p>
                                </template>
                            </FormVueSelect>
                        </div>

                        <div v-if="showVariantSelection" class="tw-col-span-12 md:tw-col-span-12 tw-mt-2 tw-mb-2">
                            <div class="tw-mb-2">
                                <h3 class="tw-text-sm tw-mb-2">{{ $t('common.product_variants') }}</h3>
                                <RestockVariants
                                    :product="selectedProduct"
                                    v-model:selectedVariants="sourcing.selected_variants"
                                />
                                <div v-if="errors.selected_variants" class="tw-text-red-500 tw-text-sm tw-mt-1">
                                    {{ $t('errors.' + errors.selected_variants) }}
                                </div>
                            </div>
                        </div>

                        <div class="tw-col-span-12 md:tw-col-span-12 tw-pb-4">
                            <h1 class="tw-ps-4 tw-border-s-2 tw-border-solid tw-border-primary-500">{{
                                $t('common.sourcing_informations') }}</h1>
                        </div>

                        <div class="tw-col-span-12 md:tw-col-span-6">
                            <FormField v-model="sourcing.product_name" :disabled="sourcing.sourcing_type === 'restock'" :label="$t('fields.product_name')" type="text"
                                :error="errors.product_name &&
                                    $t('errors.' + errors.product_name, {
                                        field: $t('fields.product_name'),
                                    })
                                    " />
                        </div>

                        <div class="tw-col-span-12 md:tw-col-span-6" v-if="!hasProductVariants">
                            <FormField v-model.number="sourcing.quantity" :label="$t('fields.quantity')" type="number"
                                :error="errors.quantity &&
                                    $t('errors.' + errors.quantity, {
                                        field: $t('fields.quantity'),
                                    })
                                    " />
                        </div>

                        <div class="tw-col-span-12 md:tw-col-span-6">
                            <FormVueSelect v-model="sourcing.destination_country" max-height="200" option-label="name" option-value="value" :options="countries"
                                :label="$t('fields.destination_country')" type="text" :error="errors.destination_country &&
                                    $t('errors.' + errors.destination_country, {
                                        field: $t('fields.destination_country'),
                                    })
                                    ">
                                <template v-slot:option="{ option }">
                                    <div>
                                        <p class="tw-max-w-[135px] tw-truncate tw-text-xs">{{ option.name }}</p>
                                    </div>
                                </template>
                            </FormVueSelect>
                        </div>

                        <div class="tw-col-span-12 md:tw-col-span-6">
                            <FormVueSelect v-model="sourcing.shipping_method" max-height="200" option-label="name" option-value="value" :options="shipping_methods"
                                :label="$t('fields.shipping_method')" type="text" :error="errors.shipping_method &&
                                    $t('errors.' + errors.shipping_method, {
                                        field: $t('fields.shipping_method'),
                                    })
                                    ">
                                <template v-slot:option="{ option }">
                                    <div>
                                        <p class="tw-max-w-[135px] tw-truncate tw-text-xs">{{ option.name }}</p>
                                    </div>
                                </template>
                            </FormVueSelect>
                        </div>

                        <div  class="tw-col-span-12 md:tw-col-span-12">
                            <FormField left-icon="solar:link-bold-duotone" v-model="sourcing.product_url" :label="$t('fields.product_url')" type="text"
                                :error="errors.product_url &&
                                    $t('errors.' + errors.product_url, {
                                        field: $t('fields.product_url'),
                                    })
                                    " />
                        </div>

                        <div class="tw-col-span-12 md:tw-col-span-12">
                            <SourcingStatus v-model:sourcing="sourcing" :errors="errors" />
                        </div>

                        <div class="tw-col-span-12 md:tw-col-span-6">
                            <FormField left-icon="solar:tag-price-bold-duotone" v-model.number="sourcing.cost_per_unit" :label="$t('fields.cost_per_unit')" type="number"
                                :error="errors.cost_per_unit &&
                                    $t('errors.' + errors.cost_per_unit, {
                                        field: $t('fields.cost_per_unit'),
                                    })
                                    " />
                        </div>

                        <div class="tw-col-span-12 md:tw-col-span-6">
                            <FormField left-icon="ic:twotone-local-shipping" v-model.number="sourcing.shipping_cost" :label="$t('fields.shipping_cost')" type="number"
                                :error="errors.shipping_cost &&
                                    $t('errors.' + errors.shipping_cost, {
                                        field: $t('fields.shipping_cost'),
                                    })
                                    " />
                        </div>

                        <div class="tw-col-span-12 md:tw-col-span-6">
                            <FormField left-icon="hugeicons:money-add-02" v-model.number="sourcing.additional_fees" :label="$t('fields.additional_fees')" type="number"
                                :error="errors.additional_fees &&
                                    $t('errors.' + errors.additional_fees, {
                                        field: $t('fields.additional_fees'),
                                    })
                                    " />
                        </div>

                        <div class="tw-col-span-12 md:tw-col-span-6">
                            <FormField left-icon="material-symbols:weight-outline" v-model.number="sourcing.weight" :label="$t('fields.weight') + ' (KG)'" type="number"
                                :error="errors.weight &&
                                    $t('errors.' + errors.weight, {
                                        field: $t('fields.weight'),
                                    })
                                    " />
                        </div>

                        <div class="tw-col-span-12 md:tw-col-span-6">
                            <FormField disabled left-icon="solar:tag-price-bold-duotone" v-model="buyingPrice" :label="$t('fields.buying-price')" type="number"
                                :error="errors.buying_price &&
                                    $t('errors.' + errors.buying_price, {
                                        field: $t('fields.buying-price'),
                                    })
                                    " />
                        </div>
                        <div class="tw-col-span-12 md:tw-col-span-6">
                            <FormField left-icon="solar:tag-price-bold-duotone" v-model.number="sourcing.selling_price" :label="$t('fields.selling-price')" type="number"
                                :error="errors.selling_price &&
                                    $t('errors.' + errors.selling_price, {
                                        field: $t('fields.selling-price'),
                                    })
                                    " />
                        </div>

                        <div class="tw-col-span-12 md:tw-col-span-12">
                            <FormField v-model="sourcing.note" :label="$t('fields.note')" type="textarea"
                                :error="errors.note &&
                                    $t('errors.' + errors.note, {
                                        field: $t('fields.note'),
                                    })
                                    " />
                        </div>

                        <div class="tw-col-span-12 md:tw-col-span-12 tw-pb-4">
                            <h1 class="tw-ps-4 tw-border-s-2 tw-border-solid tw-border-primary-500">{{
                                $t('common.sourcing_variants') }}</h1>
                        </div>

                        <div class="tw-col-span-12 md:tw-col-span-12" v-if="sourcing.sourcing_type === 'new_product'">
                            <SourcingVariants v-model:variants="sourcing.variants" />
                        </div>
                        
                        <div v-else class="tw-col-span-12 md:tw-col-span-12">
                            <p class="tw-text-gray-600 tw-text-xs">
                                {{ $t('common.variants_for_restock') }}
                            </p>
                        </div>

                    </div>
                </div>

                <div
                    class="tw-p-4 tw-text-lg tw-border-t tw-justify-end tw-border-solid tw-font-medium tw-flex dark:tw-text-gray-700 tw-text-gray-500 tw-items-center tw-gap-4 dark:tw-bg-gray-100 tw-bg-gray-50">
                    <a-button :disabled="loading" @click="onCancel" class="tw-min-w-[120px]" intent="simple">{{
                        $t("buttons.cancel") }}</a-button>
                    <a-button :disabled="loading" :loading="loading" @click="onCreate" class="tw-min-w-[120px]">{{
                        $t("buttons.create") }}</a-button>
                </div>
            </div>
        </div>
    </a-popup>

</template>

<script setup>
import { ref, defineEmits, watch, computed, onMounted } from 'vue';
import { create } from '@/api/sourcings';
import { products as productsApi } from '@/api/orders';
import { useI18n } from 'vue-i18n';
import useAlert from '@/composables/useAlert';
import FormField from "@/components/form/FormField";
import FormVueSelect from "@/components/form/FormVueSelect";
import SourcingStatus from './partials/SourcingStatus.vue';
import { countries as shipping_countries, shipping_methods as shipping_ways } from '@/config/sourcing';
import SourcingVariants from './partials/SourcingVariants.vue';
import RestockVariants from './partials/RestockVariants.vue';

const i18n = useI18n()

const emit = defineEmits(['created']);

const visible = ref(false);
const sourcing = ref({
    status: 'new',
    shipping_method: 'not-selected',
    destination_country: 'not-selected',
    sourcing_type: 'new_product',
    quantity: 0,
    shipping_cost: 0,
    cost_per_unit: 0,
    additional_fees: 0,
    weight: 0,
    buying_price: 0,
    selling_price: 0,
    variants: [],
    selected_variants: []
});

const countries = computed(() => shipping_countries.map(c => ({ ...c, name: i18n.t('countries.' + c.value) })))
const shipping_methods = computed(() => shipping_ways.map(c => ({ ...c, name: i18n.t('sourcing.shipping_methods.' + c.value) })))
const errors = ref({})
const loading = ref(false)
const productsList = ref([])
const isLoadingProducts = ref(false)
const showVariantSelection = ref(false)
const selectedProduct = ref(null)
const hasProductVariants = ref(false)

const buyingPrice = computed(() => {
    if (!(sourcing.value.shipping_cost > 0 && sourcing.value.quantity > 0)) {
        return sourcing.value.cost_per_unit;
    }
    const costPerUnit = sourcing.value.cost_per_unit ?? 0;
    const shippingAndFees = (sourcing.value.shipping_cost + sourcing.value.additional_fees) / sourcing.value.quantity;
    return Number(costPerUnit + shippingAndFees).toFixed(2);
})


watch(() => sourcing.value.sourcing_type, (newType) => {
    if (newType === 'restock') {
        // Only fetch products if we haven't already loaded them
        if (productsList.value.length === 0) {
            fetchProducts();
        }
    }
    
    // Reset product_id if switching from restock to new_product
    if (newType === 'new_product' && sourcing.value.product_id) {
        sourcing.value.product_id = null;
    }
})

watch(() => sourcing.value.product_id, (newProductId) => {
    if (newProductId) {
        const selectedProd = productsList.value.find(p => p.id === newProductId);
        if (selectedProd) {
            sourcing.value.product_name = selectedProd.name;
            // Check if product has variants and they exist
            hasProductVariants.value = selectedProd.variants && selectedProd.variants.length > 0;
            showVariantSelection.value = hasProductVariants.value;
            selectedProduct.value = selectedProd;
            
            // Reset selected variants when product changes
            sourcing.value.selected_variants = [];
        }
    } else {
        hasProductVariants.value = false;
        showVariantSelection.value = false;
        selectedProduct.value = null;
    }
})

watch(() => visible.value, (isVisible) => {
    if (isVisible) {
        // Reset form when popup opens
        resetForm();
        
        if (sourcing.value.sourcing_type === 'restock' && productsList.value.length === 0) {
            fetchProducts();
        }
    }
})

const fetchProducts = async () => {
    // If products are already loaded, don't fetch again
    if (productsList.value.length > 0) {
        return;
    }
    
    isLoadingProducts.value = true;
    errors.value.product_id = null;
    
    try {
        const response = await productsApi();
        if (response && response.data && response.data.products) {
            productsList.value = response.data.products;
            console.log("Products with variants:", productsList.value.filter(p => p.variants && p.variants.length > 0));
            
            if (productsList.value.length === 0) {
                errors.value.product_id = 'no_products_found';
            }
        } else {
            errors.value.product_id = 'failed_to_load';
        }
    } catch (error) {
        console.error('Error fetching products:', error);
        errors.value.product_id = 'failed_to_load';
    } finally {
        isLoadingProducts.value = false;
    }
}

const onCreate = async () => {
    // Prepare data for API
    const data = { ...sourcing.value, buying_price: buyingPrice.value };
    
    // If we're restocking with variants, ensure we have the right data format
    if (sourcing.value.sourcing_type === 'restock' && hasProductVariants.value) {
        // Make sure we have selected variants
        if (sourcing.value.selected_variants.length === 0) {
            errors.value.selected_variants = 'required';
            return;
        }
        
        // Validate each selected variant has a valid quantity
        let hasInvalidQuantity = false;
        sourcing.value.selected_variants.forEach(variant => {
            if (!variant.quantity || variant.quantity <= 0) {
                hasInvalidQuantity = true;
            }
        });
        
        if (hasInvalidQuantity) {
            errors.value.selected_variants = 'invalid_quantity';
            return;
        }
        
        // Set total quantity based on selected variants
        data.quantity = sourcing.value.selected_variants.reduce((total, variant) => total + variant.quantity, 0);
    }
    
    loading.value = true;
    await create(data)
        .then(
            res => {
                console.log(res);
                if (res.data.code == 'SUCCESS') {
                    // emit('update:visible', false)
                    useAlert(i18n.t('messages.created-successfully', { field: i18n.t('common.sourcing') }));
                    visible.value = false;
                    emit('created', res.data.sourcing);
                    resetForm();
                }
            },
            (err) => {
                errors.value = {};
                
                if (err?.response?.status == 422) {
                    for (let e in err.response.data.errors) {
                        errors.value[e] = err.response.data.errors[e][0];
                    }
                }
            }   
        );
    loading.value = false;
};

const onCancel = () => {
    visible.value = false;
    resetForm();
};

const resetForm = () => {
    sourcing.value = {
        status: 'new',
        shipping_method: 'not-selected',
        destination_country: 'not-selected',
        sourcing_type: 'new_product',
        quantity: 0,
        shipping_cost: 0,
        cost_per_unit: 0,
        additional_fees: 0,
        weight: 0,
        buying_price: 0,
        selling_price: 0,
        variants: [],
        selected_variants: []
    };
    errors.value = {};
    showVariantSelection.value = false;
    hasProductVariants.value = false;
    selectedProduct.value = null;
};

onMounted(() => {
    if (visible.value && sourcing.value.sourcing_type === 'restock') {
        fetchProducts();
    }
})

</script>

<style></style>